// AI insight + recommendation panels (prototype aiInsight / aiRecs).

import React from 'react';
import { Lightbulb, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { analyseDrivers } from './engine/insights';
import type { InsightOpts } from './engine/insights';
import type { Worker } from './engine/data';

export const AiInsight: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (children === null || children === undefined || children === false || children === '') return null;
  return (
    <div className='flex items-start gap-3 rounded-xl border border-info/20 bg-info/5 px-4 py-4'>
      <Lightbulb className='mt-0.5 h-5 w-5 shrink-0 text-info' aria-hidden='true' />
      <div className='min-w-0'>
        <div className='mb-1 text-xs font-semibold text-info'>Workforce insight</div>
        <div className='text-[13px] leading-relaxed text-foreground'>{children}</div>
      </div>
    </div>
  );
};

// Rank 1 = red, 2 = amber, 3+ = blue (prototype rec-priority-1/2/3).
const PRIORITY = [
  { border: 'border-l-destructive', badge: 'bg-destructive/10 text-destructive' },
  { border: 'border-l-warning', badge: 'bg-warning/10 text-warning' },
  { border: 'border-l-info', badge: 'bg-info/10 text-info' },
] as const;

export const AiRecs: React.FC<{ workers: Worker[]; opts?: InsightOpts; scopeLabel?: string }> = ({
  workers,
  opts,
  scopeLabel,
}) => {
  const steps = React.useMemo(() => analyseDrivers(workers, opts), [workers, opts]);
  if (!steps.length) return null;
  const scopeName = scopeLabel || opts?.scopeName || 'this group';
  return (
    <div className='fm-surface min-w-0 overflow-hidden rounded-xl border border-border bg-card'>
      <div className='flex flex-wrap items-center gap-2 border-b border-border px-5 py-4'>
        <ListChecks className='h-4 w-4 text-brand' aria-hidden='true' />
        <h2 className='text-[15px] font-semibold'>Recommended next steps</h2>
        <span className='rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>
          {steps.length}
        </span>
        <div className='ml-auto text-xs text-muted-foreground'>{scopeName}</div>
      </div>
      <div className='divide-y divide-border'>
        {steps.map((s, i) => {
          const p = PRIORITY[Math.min(i, PRIORITY.length - 1)];
          return (
            <div key={s.action} className={cn('flex items-start gap-3.5 border-l-2 px-4 py-4', p.border)}>
              <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', p.badge)}>
                <s.icon className='h-4 w-4' aria-hidden='true' />
              </div>
              <div className='min-w-0'>
                <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                  {s.label}
                </div>
                <div className='text-[13px] font-semibold leading-relaxed'>{s.action}</div>
                <div className='mt-1 text-xs leading-relaxed text-muted-foreground'>{s.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
